import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CONSTANT } from '@const';
import { ClientService } from '@data';
import { ApiStatus, ClientType, WaterIQUserType } from '@types';
import { convertRQueryToApiStatus } from '@utils';
import { isEmpty } from 'lodash';
import ReactGA from 'react-ga4';

import { UserTypeContext } from './UserTypeContextProvider';

const SELECTED_CLIENT_ID_KEY = 'selectedClientId';

export interface LegalEntityContextState {
	legalEntity: ClientType | undefined;
	setLegalEntity: (legalEntity: ClientType | undefined) => void;
	legalEntityList: ClientType[];
	legalEntityRequestStatus: ApiStatus;
	filteredLegalEntityList: ClientType[];
}

const contextDefaultValues: LegalEntityContextState = {
	legalEntity: undefined,
	setLegalEntity: () => {
		// do nothing
	},
	legalEntityList: [],
	legalEntityRequestStatus: ApiStatus.IDLE,
	filteredLegalEntityList: [],
};

export const LegalEntityContext = createContext<LegalEntityContextState>(contextDefaultValues);

export const LegalEntityContextProvider = ({ children }: { children: React.ReactNode }) => {
	// Context
	const { selectedUserType } = useContext(UserTypeContext);
	// State
	const [legalEntity, setLegalEntity] = useState<ClientType | undefined>(
		contextDefaultValues.legalEntity,
	);
	const [legalEntityList, setLegalEntityList] = useState<ClientType[]>(
		contextDefaultValues.legalEntityList,
	);
	const [legalEntityRequestStatus, setRequestStatus] = useState<ApiStatus>(
		contextDefaultValues.legalEntityRequestStatus,
	);
	const [filteredLegalEntityList, setFilteredLegalEntityList] = useState<ClientType[]>(
		contextDefaultValues.filteredLegalEntityList,
	);

	// Data
	const getClients = useQuery({
		queryKey: [CONSTANT.CLIENTS_APP_API_URL, 0],
		queryFn: () => ClientService.getClients(),
		enabled: selectedUserType == WaterIQUserType.WEH,
	});

	// Handle success and error from query
	useEffect(() => {
		if (getClients.data) {
			setLegalEntityList(getClients.data);
		}
		if (getClients.error) {
			setLegalEntity(undefined);
		}
	}, [getClients.data, getClients.error]);

	// Convert ReactQuery hook values into ApiStatus
	useEffect(() => {
		setRequestStatus(convertRQueryToApiStatus(getClients));
	}, [getClients]);

	// Save selected client id on sessionStorage
	useEffect(() => {
		if (legalEntity) {
			// For Google Analytics
			const savedSelectedClientId = sessionStorage.getItem(SELECTED_CLIENT_ID_KEY);
			if (savedSelectedClientId && savedSelectedClientId != legalEntity.id) {
				ReactGA.event({
					category: 'User Action',
					action: 'Change Legal Entity',
				});
			}
			//-- For Google Analytics
			sessionStorage.setItem(SELECTED_CLIENT_ID_KEY, legalEntity.id);
		}
	}, [legalEntity]);

	// Set client by saved on sessionStorage or default
	useEffect(() => {
		const savedSelectedClientId = sessionStorage.getItem(SELECTED_CLIENT_ID_KEY);
		const savedSelectedClient = legalEntityList.find(client => client.id == savedSelectedClientId);
		const clientToSelect = savedSelectedClient ? savedSelectedClient : legalEntityList[0];
		setLegalEntity(clientToSelect);
	}, [legalEntityList]);

	/**
	 * Updates filtered list to contain all clients except the currently selected one
	 */
	useEffect(() => {
		if (!isEmpty(legalEntityList) && legalEntity !== undefined) {
			const filterOutSelectedClient = legalEntityList.filter(legalEntityItem => {
				if (legalEntityItem !== undefined) {
					return legalEntityItem.id !== legalEntity.id;
				}
			});
			setFilteredLegalEntityList(filterOutSelectedClient);
		}
	}, [legalEntity, legalEntityList]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<LegalEntityContext.Provider
			value={{
				legalEntity,
				setLegalEntity,
				legalEntityList,
				legalEntityRequestStatus,
				filteredLegalEntityList,
			}}
		>
			{children}
		</LegalEntityContext.Provider>
	);
};
